<template>
  <v-card class="my-3" elevation="1">
    <v-card-title class="black--text">
      Links
      <v-spacer></v-spacer>
      <base-icon-button
        v-if="userHasEditPermissions"
        :button-icon="['fad', 'plus-circle']"
        button-color="primary"
        button-outlined
        tool-tip="Add New Link"
        @click.stop="dialog = true"
      />
      <v-icon v-if="!userHasEditPermissions" color="primary">mdi-link</v-icon>
    </v-card-title>
    <v-card-subtitle v-if="links && !links.length && userHasEditPermissions" class="pt-3">
      <span>Click the plus icon to add a link</span>
    </v-card-subtitle>
    <v-list v-if="links.length" dense two-line>
      <v-list-item v-for="(link, i) in links" :key="i" @click="openUrl(link.url)">
        <v-list-item-avatar>
          <v-icon color="primary" v-text="'mdi-chevron-right'"></v-icon>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title class="pb-1">
            {{ link.name }} {{ userHasEditPermissions ? ` (${truncateText(link.url)})` : '' }}
          </v-list-item-title>
          <v-list-item-subtitle>{{ link.description }}</v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action v-if="userHasEditPermissions" @click="deleteLink($event, i)">
          <base-icon-button
            :button-icon="['fad', 'trash-alt']"
            button-color="danger"
            button-outlined
            tool-tip="Delete Link"
          ></base-icon-button>
        </v-list-item-action>
      </v-list-item>
    </v-list>
    <v-dialog v-if="userHasEditPermissions" v-model="dialog" width="500">
      <v-card>
        <v-card-title class="pb-0">
          <span class="headline">Add Link</span>
        </v-card-title>
        <v-card-text class="pb-0">
          <v-container class="py-0 px-1">
            <v-form ref="form" v-model="valid">
              <v-row>
                <v-col cols="12" class="pb-0">
                  <v-text-field
                    v-model="name"
                    autocomplete="off"
                    :rules="nameRules"
                    label="Name"
                    hint="What would you link to be called?"
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" class="pb-0">
                  <v-text-field
                    v-model="url"
                    autocomplete="off"
                    :rules="urlRules"
                    label="Url"
                    hint="This is where your link will direct the user to"
                    required
                    @focus="prePopulateUrl"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" class="pb-0">
                  <v-text-field
                    v-model="description"
                    autocomplete="off"
                    :rules="descriptionRules"
                    label="Description"
                    hint="Give a brief description"
                    required
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn outlined @click="cancel">Cancel</v-btn>
          <v-btn :loading="loading" :disabled="!valid" color="primary" @click="add">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <prompt ref="deletePrompt">
      <template #body>Are you sure you want to delete this Link?</template>
    </prompt>
  </v-card>
</template>

<script>
import Prompt from '../dashboard/Prompt';

export default {
  name: 'Links',
  components: { Prompt },
  props: {
    links: {
      type: Array,
      default: () => [],
    },
    userHasEditPermissions: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      valid: false,
      dialog: false,
      loading: false,
      name: '',
      nameRules: [v => !!v || 'Name is required', v => v.length <= 30 || 'Name must be less than 30 characters'],
      url: '',
      urlRules: [v => !!v || 'Url is required', v => this.isValidUrl(v) || 'You must provide a valid url!'],
      description: '',
      descriptionRules: [v => v.length <= 100 || 'Description must be less than 100 characters'],
    };
  },
  methods: {
    openUrl(url) {
      window.open(url, '_blank');
    },
    cancel() {
      this.name = '';
      this.url = '';
      this.description = '';
      this.$refs.form.resetValidation();
      this.dialog = false;
    },
    async add() {
      this.loading = true;

      const newLinks = [
        ...this.links,
        {
          name: this.name,
          url: this.url,
          description: this.description,
        },
      ];

      try {
        this.$emit('update:links', newLinks);
        await this.$store.dispatch('video/update');
      } catch (err) {
        this.loading = false;
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Error!',
          text: `A problem occurred while trying to add your link: ${err.message}`,
          duration: 5000,
        });
        return;
      }

      this.$notify({
        group: 'app',
        type: 'success',
        title: 'Success!',
        text: 'Your link was saved!',
      });

      this.loading = false;
      this.name = '';
      this.url = '';
      this.description = '';
      this.$refs.form.resetValidation();
      this.dialog = false;
    },
    async deleteLink(event, index) {
      event.stopPropagation();
      const deleted = this.$refs.deletePrompt.open('Delete Link', 'Delete', {});
      if (await deleted) {
        const newLinks = [...this.links];
        newLinks.splice(index, 1);

        try {
          this.$emit('update:links', newLinks);
          await this.$store.dispatch('video/update');
        } catch (err) {
          this.loading = false;
          this.$notify({
            group: 'app',
            type: 'error',
            title: 'Error!',
            text: `A problem occurred while trying to delete your link: ${err.message}`,
            duration: 5000,
          });
          return;
        }

        this.$notify({
          group: 'app',
          type: 'success',
          title: 'Success!',
          text: 'Your link was deleted!',
        });
      }
    },
    isValidUrl(string) {
      try {
        const url = new URL(string);
        if (url) return true;
      } catch (e) {
        return false;
      }
    },
    prePopulateUrl() {
      if (this.url === '' || !this.url) {
        this.url = 'https://';
      }
    },
    truncateText(text) {
      return text.length > 30 ? `${text.slice(0, 30)}...` : text;
    }
  },
};
</script>
